import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import DatePicker from "react-bootstrap-daterangepicker";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  CreditCard,
  Download,
  Eye,
  Filter,
  Home,
  Loader as SpinnerIcon,
  Mail,
  Phone,
  RefreshCw,
  RotateCw,
  Search,
  UploadCloud,
  User,
  X,
} from "react-feather";

import { channelsApi } from "./api/channelsApi";
import exportService from "../utils/exportService";
import PrintReportPreview from "../utils/PrintReportPreview";
import { useNotification } from "../../context/NotificationContext";

// Ensure the daterangepicker CSS is loaded somewhere globally in your app.
// If your app already imports this once globally, remove the line below.
import "bootstrap-daterangepicker/daterangepicker.css";

const AUTO_REFRESH_MS = 30000;
const DEFAULT_CURRENCY = "INR";

const ACK_OPTIONS = [
  { value: "all", label: "All acknowledgement states" },
  { value: "acked", label: "Acknowledged" },
  { value: "pending", label: "Pending acknowledgement" },
  { value: "failed", label: "Acknowledgement failed" },
];

const SYNC_OPTIONS = [
  { value: "all", label: "All StayX sync states" },
  { value: "synced", label: "Synced to StayX" },
  { value: "not_synced", label: "Not Synced" },
  { value: "failed", label: "Sync Failed" },
];

const DATE_PRESETS = [
  { key: "all", label: "All Dates" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "this_month", label: "This Month" },
  { key: "next_month", label: "Next Month" },
  { key: "last_month", label: "Last Month" },
  { key: "next_30_days", label: "Next 30 Days" },
  { key: "custom", label: "Custom Range" },
];

const STATUS_META = {
  new: { label: "New", className: "ns-chip ns-chip--blue" },
  confirmed: { label: "Confirmed", className: "ns-chip ns-chip--green" },
  modified: { label: "Modified", className: "ns-chip ns-chip--amber" },
  cancelled: { label: "Cancelled", className: "ns-chip ns-chip--red" },
  unknown: { label: "Unknown", className: "ns-chip ns-chip--slate" },
};

const safeLower = (value) => String(value || "").trim().toLowerCase();

const coerceMoment = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.clone();
  const m = moment(value);
  return m.isValid() ? m : null;
};

const formatDate = (value) => {
  const m = coerceMoment(value);
  return m ? m.format("DD MMM YYYY") : "—";
};

const formatDateTime = (value) => {
  const m = coerceMoment(value);
  return m ? m.format("DD MMM YYYY • hh:mm A") : "—";
};

const formatRelative = (value) => {
  const m = coerceMoment(value);
  return m ? m.fromNow() : "Not available";
};

const formatFullName = (...parts) =>
  parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ")
    .trim() || null;

const toCurrency = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  return `${currency || DEFAULT_CURRENCY} ${amount}`;
};

const getAckState = (row) => {
  if (safeLower(row?.ack_status) === "failed" || row?.ack_failed) return "failed";
  if (row?.is_acked) return "acked";
  return "pending";
};

const getSyncState = (row) => {
  const state = safeLower(row?.stayx_sync_status || row?.sync_state);
  if (state === "failed" || row?.import_failed) return "failed";
  if (state === "synced" || row?.is_imported) return "synced";
  return "not_synced";
};


const formatExpectedWindow = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";

  const formatHour = (h, m = "00") => {
    const hour = Number(h);
    const minute = Number(m || 0);
    const dt = new Date();
    dt.setHours(hour, minute, 0, 0);

    return dt.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: minute ? "2-digit" : undefined,
      hour12: true,
    });
  };

  const between = raw.match(
    /^between\s+(\d{1,2}):(\d{2})\s+and\s+(\d{1,2}):(\d{2})$/i
  );
  if (between) {
    const start = formatHour(between[1], between[2]);
    const end = formatHour(between[3], between[4]);
    return `${start} - ${end}`;
  }

  const exact = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (exact) {
    return formatHour(exact[1], exact[2]);
  }

  return raw;
};

const formatPaymentCollectLabel = (value) => {
  if (value === "property") return "Hotel collect";
  if (value === "ota") return "Collected by OTA";
  return value || "—";
};

const formatOccupancySummary = ({ adults = 0, children = 0, infants = 0 } = {}) => {
  const parts = [];
  if (Number(adults) > 0) parts.push(`${adults} Adult${Number(adults) > 1 ? "s" : ""}`);
  if (Number(children) > 0) parts.push(`${children} Child${Number(children) > 1 ? "ren" : ""}`);
  if (Number(infants) > 0) parts.push(`${infants} Infant${Number(infants) > 1 ? "s" : ""}`);
  return parts.join(" • ") || "—";
};

const getPresetRange = (key) => {
  const businessMoment = moment().startOf("day");

  switch (key) {
    case "today":
      return {
        startDate: businessMoment.clone(),
        endDate: businessMoment.clone(),
      };
    case "tomorrow":
      return {
        startDate: businessMoment.clone().add(1, "day"),
        endDate: businessMoment.clone().add(1, "day"),
      };
    case "yesterday":
      return {
        startDate: businessMoment.clone().subtract(1, "day"),
        endDate: businessMoment.clone().subtract(1, "day"),
      };
    case "this_week":
      return {
        startDate: businessMoment.clone().startOf("week"),
        endDate: businessMoment.clone().endOf("week"),
      };
    case "this_month":
      return {
        startDate: businessMoment.clone().startOf("month"),
        endDate: businessMoment.clone(),
      };
    case "next_month":
      return {
        startDate: businessMoment.clone().add(1, "month").startOf("month"),
        endDate: businessMoment.clone().add(1, "month").endOf("month"),
      };
    case "last_month":
      return {
        startDate: businessMoment.clone().subtract(1, "month").startOf("month"),
        endDate: businessMoment.clone().subtract(1, "month").endOf("month"),
      };
    case "next_30_days":
      return {
        startDate: businessMoment.clone(),
        endDate: businessMoment.clone().add(30, "day"),
      };
    default:
      return null;
  }
};

const formatRangeLabel = (selectedPreset, dateRange) => {
  const preset = DATE_PRESETS.find((item) => item.key === selectedPreset);
  if (selectedPreset === "all") return "All Dates";
  if (selectedPreset !== "custom" && preset) return preset.label;
  if (dateRange?.startDate && dateRange?.endDate) {
    return `${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`;
  }
  return "Select Date Range";
};

const stayBucket = (row) => {
  const today = moment().startOf("day");
  const checkIn = coerceMoment(row.arrival_date || row.check_in);
  if (!checkIn) return "unknown";
  return checkIn.startOf("day").isBefore(today) ? "past" : "upcoming";
};

const extractSpecialRequest = (notes) => {
  const lines = String(notes || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

  const ignorePrefixes = [
    "approximate time of arrival:",
    "meal plan:",
    "breakfast costs",
    "dinner costs",
    "smoking preference:",
    "payment collect:",
    "ota commission:",
  ];

  const kept = lines.filter(
    (line) => !ignorePrefixes.some((prefix) => line.toLowerCase().startsWith(prefix))
  );

  return kept.join(" • ") || null;
};

const extractExpectedArrivalText = (notes, arrivalHour = null) => {
  const match = String(notes || "").match(/Approximate time of arrival:\s*(.+)/i);
  return match?.[1]?.trim() || arrivalHour || null;
};

const normalizeRows = (rows = [], fallbackCurrency = DEFAULT_CURRENCY) => {
  return rows.map((row, index) => {
    const rawPayload = row.raw_payload || {};
    const attrs = rawPayload?.attributes || {};
    const roomsJson = Array.isArray(row.rooms_json)
      ? row.rooms_json
      : Array.isArray(row.rooms_breakdown)
      ? row.rooms_breakdown
      : [];

    const arrivalDate = row.arrival_date || attrs.arrival_date || null;
    const departureDate = row.departure_date || attrs.departure_date || null;
    const status = safeLower(row.status || attrs.status || "unknown") || "unknown";
    const expectedArrivalText =
      row.expected_arrival_text ||
      row.expected_arrival_time ||
      extractExpectedArrivalText(attrs.notes, attrs.arrival_hour);

    const primaryGuestName =
      row.guest_name ||
      row.primary_guest_name ||
      formatFullName(attrs?.customer?.name, attrs?.customer?.surname) ||
      formatFullName(roomsJson?.[0]?.guests?.[0]?.name, roomsJson?.[0]?.guests?.[0]?.surname) ||
      "Guest";

    const roomSummary = {
      room_type_display:
        row.room_type_display ||
        row.primary_pms_room_type_name ||
        row.primary_room_type_name ||
        row.primary_room_type_id ||
        "—",
      rate_plan_display:
        row.rate_plan_display ||
        row.primary_rate_plan_title ||
        row.primary_rate_plan_id ||
        "—",
    };

    return {
      key: row.latest_revision_id || row.revision_id || row.booking_id || `booking-${index}`,
      revision_id: row.revision_id || null,
      latest_revision_id: row.latest_revision_id || row.revision_id || null,
      booking_id: row.booking_id || null,
      booking_unique_id: row.booking_unique_id || attrs.unique_id || null,
      ota_name: row.ota_name || attrs.ota_name || null,
      ota_reservation_code: row.ota_reservation_code || attrs.ota_reservation_code || null,
      channel_id: row.channel_id || attrs.channel_id || null,

      guest_name: primaryGuestName,
      guest_first_name: row.guest_first_name || row.primary_guest_first_name || attrs?.customer?.name || null,
      guest_last_name: row.guest_last_name || row.primary_guest_last_name || attrs?.customer?.surname || null,
      guest_email: row.guest_email || row.primary_guest_email || attrs?.customer?.mail || null,
      guest_phone: row.guest_phone || row.primary_guest_phone || attrs?.customer?.phone || null,
      guest_country: row.guest_country || row.primary_guest_country || attrs?.customer?.country || null,
      guest_city: row.guest_city || row.primary_guest_city || attrs?.customer?.city || null,
      guest_address: row.guest_address || row.primary_guest_address || attrs?.customer?.address || null,
      guest_zip: row.guest_zip || row.primary_guest_zip || attrs?.customer?.zip || null,
      is_genius: !!attrs?.customer?.meta?.is_genius,

      arrival_date: arrivalDate,
      departure_date: departureDate,
      expected_arrival_time: row.expected_arrival_time || attrs.arrival_hour || null,
      expected_arrival_text: expectedArrivalText,

      inserted_at: row.inserted_at_utc || row.inserted_at || attrs.inserted_at || row.created_at || null,
      inserted_at_display: row.inserted_at_display || null,
      acked_at: row.acked_at_utc || row.acked_at || null,
      acked_at_display: row.acked_at_display || null,
      stayx_synced_at: row.stayx_synced_at_utc || row.stayx_synced_at || null,
      stayx_synced_at_display: row.stayx_synced_at_display || null,
      last_activity_at: row.last_activity_at_utc || row.last_activity_at || null,
      last_activity_at_display: row.last_activity_at_display || null,

      status,
      status_meta: STATUS_META[status] || STATUS_META.unknown,
      is_cancelled: row.is_cancelled === true || status === "cancelled",
      is_acked: !!row.is_acked,
      ack_error: row.ack_error || row.ack_error_message || null,

      stayx_sync_status: row.stayx_sync_status || "not_synced",
      stayx_booking_id: row.stayx_booking_id || null,
      stayx_sync_error: row.stayx_sync_error || row.import_error || null,
      is_imported: !!row.is_imported,
      import_failed: !!row.import_failed,

      rooms_count: Number(row.rooms_count || roomsJson.length || 0),
      nights_count: Number(row.nights_count || 0),
      adults_count: Number(row.adults_count ?? attrs?.occupancy?.adults ?? 0),
      children_count: Number(row.children_count ?? attrs?.occupancy?.children ?? 0),
      infants_count: Number(row.infants_count ?? attrs?.occupancy?.infants ?? 0),
      occupancy_summary: formatOccupancySummary({
        adults: row.adults_count ?? attrs?.occupancy?.adults ?? 0,
        children: row.children_count ?? attrs?.occupancy?.children ?? 0,
        infants: row.infants_count ?? attrs?.occupancy?.infants ?? 0,
      }),

      amount: row.amount || row.booking_amount || attrs.amount || null,
      currency: row.currency || row.booking_currency || attrs.currency || fallbackCurrency,
      amount_paid: row.amount_paid ?? null,
      amount_paid_currency: row.amount_paid_currency || row.booking_currency || attrs.currency || fallbackCurrency,
      amount_paid_label: row.amount_paid_label || toCurrency(row.amount_paid, row.amount_paid_currency || row.currency),
      ota_commission: row.ota_commission ?? attrs.ota_commission ?? null,
      payment_collect: row.payment_collect || attrs.payment_collect || null,
      payment_type: row.payment_type || attrs.payment_type || null,

      special_request: extractSpecialRequest(row.special_request) || extractSpecialRequest(attrs.notes),
      smoking_preference: row.smoking_preference || roomsJson?.[0]?.meta?.smoking_preferences || null,
      meal_plan_text: row.meal_plan_text || roomsJson?.[0]?.meta?.meal_plan || null,

      room_type_display: roomSummary.room_type_display,
      room_type_id: row.primary_room_type_id || null,
      pms_room_type_id: row.primary_pms_room_type_id || null,
      pms_room_type_name: row.primary_pms_room_type_name || null,

      rate_plan_display: roomSummary.rate_plan_display,
      rate_plan_id: row.primary_rate_plan_id || null,
      rate_plan_title: row.primary_rate_plan_title || null,
      primary_room_guest_name: row.primary_room_guest_name || null,

      rooms_breakdown: roomsJson,
      raw_payload: rawPayload,
      notes: attrs.notes || null,
    };
  });
};

const buildExportHeaders = () => [
  { key: "received", label: "Received" },
  { key: "ota", label: "OTA" },
  { key: "guest", label: "Guest" },
  { key: "bookingId", label: "Booking ID" },
  { key: "revisionId", label: "Revision ID" },
  { key: "guestPreference", label: "Guest Preference" },
  { key: "arrival", label: "Arrival" },
  { key: "departure", label: "Departure" },
  { key: "guests", label: "Guests" },
  { key: "rooms", label: "Rooms" },
  { key: "nights", label: "Nights" },
  { key: "status", label: "Status" },
  { key: "ack", label: "Ack" },
  { key: "stayx", label: "StayX Sync" },
  { key: "amount", label: "Amount" },
];

const buildExportData = (rows, currencyCode) =>
  rows.map((row) => ({
    received: row.inserted_at_display || formatDateTime(row.inserted_at),
    ota: row.ota_name || "—",
    guest: row.guest_name || "—",
    bookingId: row.booking_id || "—",
    revisionId: row.latest_revision_id || row.revision_id || "—",
    guestPreference: row.special_request || "—",
    arrival: formatDate(row.arrival_date),
    departure: formatDate(row.departure_date),
    guests: row.adults_count + row.children_count + row.infants_count,
    rooms: row.rooms_count || 0,
    nights: row.nights_count || 0,
    status: row.status_meta?.label || row.status || "—",
    ack: getAckState(row),
    stayx: getSyncState(row),
    amount: toCurrency(row.amount, row.currency || currencyCode),
  }));

const toCsvValue = (value) => {
  const stringified = String(value ?? "");
  if (stringified.includes(",") || stringified.includes('"') || stringified.includes("\n")) {
    return `"${stringified.replace(/"/g, '""')}"`;
  }
  return stringified;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const exportRowsToCsvFallback = (rows, currencyCode) => {
  const headers = buildExportHeaders();
  const exportData = buildExportData(rows, currencyCode);
  const csv = [
    headers.map((item) => item.label).join(","),
    ...exportData.map((item) =>
      headers.map((header) => toCsvValue(item[header.key])).join(",")
    ),
  ].join("\n");

  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "ota-bookings.csv");
};

const tryUtilityExport = ({ type, rows, headers, currencyCode, notify }) => {
  const exportData = buildExportData(rows, currencyCode);

  try {
    if (type === "excel") {
      if (typeof exportService?.exportExcel === "function") {
        exportService.exportExcel({ fileName: "ota-bookings", data: exportData, headers });
        return true;
      }
      if (typeof exportService?.exportToExcel === "function") {
        exportService.exportToExcel({ fileName: "ota-bookings", data: exportData, headers });
        return true;
      }
      if (typeof exportService?.downloadExcel === "function") {
        exportService.downloadExcel({ fileName: "ota-bookings", data: exportData, headers });
        return true;
      }
    }

    if (type === "csv") {
      if (typeof exportService?.exportCSV === "function") {
        exportService.exportCSV({ fileName: "ota-bookings", data: exportData, headers });
        return true;
      }
      if (typeof exportService?.exportToCsv === "function") {
        exportService.exportToCsv({ fileName: "ota-bookings", data: exportData, headers });
        return true;
      }
      if (typeof exportService?.downloadCSV === "function") {
        exportService.downloadCSV({ fileName: "ota-bookings", data: exportData, headers });
        return true;
      }
    }
  } catch (error) {
    notify("error", error?.message || `Failed to export ${type.toUpperCase()}.`);
    return true;
  }

  return false;
};

const Loader = ({ label = "Loading OTA booking activity..." }) => (
  <div className="ns-loader-card">
    <div className="ns-loader-ring">
      <SpinnerIcon size={28} className="ns-spin" />
    </div>
    <div className="ns-loader-title">Working on live booking activity</div>
    <div className="ns-loader-copy">{label}</div>
  </div>
);

const MetricCard = ({ title, value, meta, tone = "blue", icon }) => (
  <div className={`ns-metric-card ns-metric-card--${tone}`}>
    <div className="ns-metric-card__top">
      <div>
        <div className="ns-metric-card__title">{title}</div>
        <div className="ns-metric-card__value">{value}</div>
      </div>
      <div className={`ns-metric-card__icon ns-metric-card__icon--${tone}`}>{icon}</div>
    </div>
    <div className="ns-metric-card__meta">{meta}</div>
  </div>
);

const DetailField = ({ label, value, icon = null, emphasize = false }) => (
  <div className="ns-detail-field">
    <div className="ns-detail-field__label">
      {icon ? <span className="ns-detail-field__icon">{icon}</span> : null}
      <span>{label}</span>
    </div>
    <div className={`ns-detail-field__value ${emphasize ? "ns-detail-field__value--strong" : ""}`}>
      {value || "—"}
    </div>
  </div>
);

export default function BookingDashboardModern() {
  const { notify } = useNotification();

  const currencyCode =
    String(
      localStorage.getItem("currencycode") ||
        localStorage.getItem("currencyCode") ||
        localStorage.getItem("currency") ||
        DEFAULT_CURRENCY
    ).trim() || DEFAULT_CURRENCY;

  const propertyName =
    localStorage.getItem("property_name") ||
    localStorage.getItem("propertyName") ||
    "Your Property Name";

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [ackFilter, setAckFilter] = useState("all");
  const [syncFilter, setSyncFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("all");

  const [selectedPreset, setSelectedPreset] = useState("this_month");
  const [dateRange, setDateRange] = useState(() => getPresetRange("this_month"));
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState({});
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [pdfVisible, setPdfVisible] = useState(false);

  const inFlightRef = useRef(false);
  const refreshRef = useRef(null);

  const syncEnabled = typeof channelsApi.importBookingRevision === "function";

  const exportHeaders = useMemo(() => buildExportHeaders(), []);
  const exportData = useMemo(() => buildExportData(rows, currencyCode), [rows, currencyCode]);
  const totalsForPageFooter = useMemo(() => ({
    guests: rows.reduce(
      (acc, row) => acc + Number(row.adults_count || 0) + Number(row.children_count || 0) + Number(row.infants_count || 0),
      0
    ),
    nights: rows.reduce((acc, row) => acc + Number(row.nights_count || 0), 0),
  }), [rows]);

  const setBusy = useCallback((key, value) => {
    setActionBusy((prev) => ({ ...prev, [key]: !!value }));
  }, []);

  const loadSummary = useCallback(async () => {
    if (typeof channelsApi.bookingDashboardSummary !== "function") return null;
    const response = await channelsApi.bookingDashboardSummary({ provider_code: "channex" });
    return response?.data?.data || response?.data || null;
  }, []);

  const loadRows = useCallback(async ({ forceSync = false } = {}) => {
    if (!forceSync && typeof channelsApi.listBookingRevisions === "function") {
      const response = await channelsApi.listBookingRevisions({ provider_code: "channex", limit: 100 });
      const data = response?.data?.data || [];
      return normalizeRows(data, currencyCode);
    }

    const response = await channelsApi.syncBookingRevisions({
      provider_code: "channex",
      ack: false,
      limit: 100,
    });

    const data = response?.data?.data || [];
    return normalizeRows(data, currencyCode);
  }, [currencyCode]);

  const loadAll = useCallback(
    async ({ forceSync = false, silent = false } = {}) => {
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      if (!silent) setLoading(true);

      try {
        const [summaryData, rowsData] = await Promise.all([
          loadSummary(),
          loadRows({ forceSync }),
        ]);

        setSummary(summaryData);
        setRows(rowsData);
        setLastLoadedAt(moment().toISOString());
      } catch (error) {
        if (!silent) {
          notify("error", error?.response?.data?.message || "Failed to load OTA bookings. Please try again.");
        }
      } finally {
        inFlightRef.current = false;
        if (!silent) setLoading(false);
      }
    },
    [loadRows, loadSummary, notify]
  );

  useEffect(() => {
    loadAll();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadAll({ silent: true });
      }
    };

    refreshRef.current = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadAll({ silent: true });
      }
    }, AUTO_REFRESH_MS);

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadAll]);

  useEffect(() => {
    const closeOnOutside = () => setDownloadOpen(false);
    if (downloadOpen) {
      window.addEventListener("click", closeOnOutside);
    }
    return () => window.removeEventListener("click", closeOnOutside);
  }, [downloadOpen]);

  const onSyncNow = async () => {
    setSyncing(true);
    try {
      const pendingStayXSyncCount = rows.filter((row) => getSyncState(row) !== "synced").length;

      await loadAll({ forceSync: true, silent: true });

      const bulkStayXSync =
        channelsApi.syncPendingStayXBookings ||
        channelsApi.syncUnsyncedStayXBookings ||
        channelsApi.syncPendingToStayX ||
        null;

      if (typeof bulkStayXSync === "function" && pendingStayXSyncCount > 0) {
        await bulkStayXSync({ provider_code: "channex" });
        notify("success", "Booking feed refreshed and StayX sync request submitted.");
      } else if (pendingStayXSyncCount > 0) {
        notify(
          "success",
          `Booking feed refreshed. ${pendingStayXSyncCount} booking(s) still await StayX sync. Bulk StayX sync endpoint is not available yet.`
        );
      } else {
        notify("success", "Booking feed refreshed successfully.");
      }

      await loadAll({ silent: true });
    } catch (error) {
      notify("error", error?.response?.data?.message || "Failed to sync booking feed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const onRefresh = async () => {
    await loadAll();
  };

  const onRetryFailedAcks = async () => {
    if (typeof channelsApi.retryFailedAcks !== "function") {
      notify("error", "Retry acknowledge route is not available yet.");
      return;
    }

    setRetrying(true);
    try {
      await channelsApi.retryFailedAcks({ provider_code: "channex", limit: 20 });
      notify("success", "Retry failed acknowledgement completed.");
      await loadAll({ silent: true });
    } catch (error) {
      notify("error", error?.response?.data?.message || "Failed to retry acknowledgements.");
    } finally {
      setRetrying(false);
    }
  };

  const onAcknowledgeOne = async (row) => {
    if (typeof channelsApi.acknowledgeBookingRevision !== "function") {
      notify("error", "Acknowledge endpoint is not available yet.");
      return;
    }

    if (!row?.latest_revision_id && !row?.revision_id) return;

    const revisionId = row.latest_revision_id || row.revision_id;
    setBusy(`ack-${revisionId}`, true);

    try {
      await channelsApi.acknowledgeBookingRevision(revisionId, { provider_code: "channex" });
      notify("success", "Booking revision acknowledged successfully!");
      await loadAll({ silent: true });
    } catch (error) {
      notify("error", error?.response?.data?.message || "Failed to acknowledge booking revision. Please try again.");
    } finally {
      setBusy(`ack-${revisionId}`, false);
    }
  };

  const onSyncToStayX = async (row) => {
    if (!syncEnabled) {
      notify("error", "StayX sync endpoint is not available yet.");
      return;
    }

    const revisionId = row.latest_revision_id || row.revision_id;
    if (!revisionId) return;

    setBusy(`sync-${revisionId}`, true);
    try {
      await channelsApi.importBookingRevision(revisionId, { provider_code: "channex" });
      notify("success", "Booking synced to StayX successfully!");
      await loadAll({ silent: true });
    } catch (error) {
      notify("error", error?.response?.data?.message || "Failed to sync booking to StayX. Please try again.");
    } finally {
      setBusy(`sync-${revisionId}`, false);
    }
  };

  const channelOptions = useMemo(() => {
    const unique = Array.from(new Set(rows.map((row) => row.ota_name).filter(Boolean))).sort();
    return [{ value: "all", label: "All OTAs" }, ...unique.map((item) => ({ value: item, label: item }))];
  }, [rows]);

  const applyDatePreset = useCallback((presetKey) => {
    setSelectedPreset(presetKey);
    if (presetKey === "all") {
      setDateRange(null);
      return;
    }

    if (presetKey === "custom") return;

    const nextRange = getPresetRange(presetKey);
    setDateRange(nextRange);
  }, []);

  const filteredRows = useMemo(() => {
    const query = safeLower(searchText);

    return rows.filter((row) => {
      const ackState = getAckState(row);
      const syncState = getSyncState(row);
      const bucket = stayBucket(row);

      const matchesSearch =
        !query ||
        safeLower(row.guest_name).includes(query) ||
        safeLower(row.guest_email).includes(query) ||
        safeLower(row.guest_phone).includes(query) ||
        safeLower(row.booking_id).includes(query) ||
        safeLower(row.booking_unique_id).includes(query) ||
        safeLower(row.latest_revision_id || row.revision_id).includes(query) ||
        safeLower(row.ota_reservation_code).includes(query);

      const matchesAck = ackFilter === "all" || ackState === ackFilter;
      const matchesSync = syncFilter === "all" || syncState === syncFilter;
      const matchesChannel = channelFilter === "all" || row.ota_name === channelFilter;

      const matchesTab =
        activeTab === "all"
          ? true
          : activeTab === "upcoming"
          ? bucket === "upcoming"
          : activeTab === "past"
          ? bucket === "past"
          : activeTab === "attention"
          ? ackState === "failed" || syncState === "failed"
          : true;

      // Booking activity dashboard should filter by RECEIVED date first,
      // not arrival date. Otherwise future-stay bookings created today disappear
      // from the working table even though they are fresh activity.
      const activityDate = coerceMoment(row.inserted_at || row.last_activity_at || row.arrival_date);
      const matchesDate =
        !dateRange?.startDate || !dateRange?.endDate || !activityDate
          ? true
          : activityDate.isBetween(
              dateRange.startDate.clone().startOf("day"),
              dateRange.endDate.clone().endOf("day"),
              undefined,
              "[]"
            );

      return matchesSearch && matchesAck && matchesSync && matchesChannel && matchesTab && matchesDate;
    });
  }, [rows, searchText, ackFilter, syncFilter, channelFilter, activeTab, dateRange]);

  const derivedSummary = useMemo(() => {
    // Keep KPI cards aligned with the currently visible working dataset.
    const pendingAck = filteredRows.filter((row) => getAckState(row) === "pending").length;
    const failedAck = filteredRows.filter((row) => getAckState(row) === "failed").length;
    const syncedToStayX = filteredRows.filter((row) => getSyncState(row) === "synced").length;
    const upcomingBookings = filteredRows.filter((row) => stayBucket(row) === "upcoming").length;

    return {
      pending_ack: pendingAck,
      failed_ack: failedAck,
      synced_to_stayx: syncedToStayX,
      upcoming_bookings: upcomingBookings,
      total_visible: filteredRows.length,
      total_loaded: rows.length,
      last_sync_at:
        summary?.data?.last_sync_at || summary?.last_sync_at || lastLoadedAt || null,
      last_sync_at_display:
        summary?.data?.last_sync_at_display || summary?.last_sync_at_display || null,
    };
  }, [filteredRows, rows.length, summary, lastLoadedAt]);

  const tabCounts = useMemo(() => ({
    all: filteredRows.length,
    upcoming: filteredRows.filter((row) => stayBucket(row) === "upcoming").length,
    past: filteredRows.filter((row) => stayBucket(row) === "past").length,
    attention: filteredRows.filter((row) => getAckState(row) === "failed" || getSyncState(row) === "failed").length,
  }), [filteredRows]);

  const openDrawer = (row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const handleDateChange = ({ start, end, label }) => {
    const normalizedLabel = safeLower(label).replace(/\s+/g, "_");
    const knownPreset = DATE_PRESETS.find((item) => item.key === normalizedLabel);
    setSelectedPreset(knownPreset ? knownPreset.key : "custom");
    setDateRange({
      startDate: start.clone().startOf("day"),
      endDate: end.clone().endOf("day"),
    });
  };

  const handleExportCSV = () => {
    const handled = tryUtilityExport({
      type: "csv",
      rows: filteredRows,
      headers: exportHeaders,
      currencyCode,
      notify,
    });

    if (!handled) {
      exportRowsToCsvFallback(filteredRows, currencyCode);
      notify("success", "CSV downloaded successfully.");
    }

    setDownloadOpen(false);
  };

  const handleExportExcel = () => {
    const handled = tryUtilityExport({
      type: "excel",
      rows: filteredRows,
      headers: exportHeaders,
      currencyCode,
      notify,
    });

    if (!handled) {
      exportRowsToCsvFallback(filteredRows, currencyCode);
      notify("success", "Excel utility not found. CSV downloaded instead.");
    } else {
      notify("success", "Export started successfully.");
    }

    setDownloadOpen(false);
  };

  const handleOpenPdfPreview = () => {
    setPdfVisible(true);
    setDownloadOpen(false);
  };

  const handleExportPDF = async () => {
    // Keep intentionally thin. PrintReportPreview is already wired with apiEndpoint/apiUrl.
    notify("success", "PDF generation started.");
  };

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid ns-ota-page">
        <div className="ns-hero-panel">
          <div className="ns-hero-panel__copy">
            <h1 className="ns-page-title">OTA Bookings</h1>
            <p className="ns-page-subtitle">
              Review incoming reservations, monitor acknowledgement health, and manage StayX sync from one modern operational workspace.
            </p>
          </div>

          <div className="ns-hero-panel__meta">
            <div className="ns-mini-stat">
              <div className="ns-mini-stat__label">Last sync</div>
              <div className="ns-mini-stat__value">
                {derivedSummary.last_sync_at_display || formatRelative(derivedSummary.last_sync_at)}
              </div>
            </div>
          </div>
        </div>

        <div className="ns-command-card">
          <div className="ns-command-card__top">
            <div className="ns-toolbar-grid">
              <div className="ns-date-picker-wrap">
                <DatePicker
                  initialSettings={{
                    singleDatePicker: false,
                    showDropdowns: true,
                    startDate: (dateRange?.startDate || getPresetRange("this_month")?.startDate || moment().startOf("month")).toDate(),
                    endDate: (dateRange?.endDate || getPresetRange("this_month")?.endDate || moment()).toDate(),
                    minDate: moment().subtract(2, "year").toDate(),
                    ranges: {
                      Today: [moment().toDate(), moment().toDate()],
                      Tomorrow: [moment().add(1, "day").toDate(), moment().add(1, "day").toDate()],
                      "This Week": [moment().startOf("week").toDate(), moment().endOf("week").toDate()],
                      "This Month": [moment().startOf("month").toDate(), moment().toDate()],
                      "Last Month": [
                        moment().subtract(1, "month").startOf("month").toDate(),
                        moment().subtract(1, "month").endOf("month").toDate(),
                      ],
                      "Next 30 Days": [moment().toDate(), moment().add(30, "day").toDate()],
                      "Next Month": [
                        moment().add(1, "month").startOf("month").toDate(),
                        moment().add(1, "month").endOf("month").toDate(),
                      ],
                    },
                    locale: {
                      format: "DD MMM YYYY",
                      applyLabel: "Select",
                      cancelLabel: "Cancel",
                    },
                  }}
                  onCallback={(start, end, label) => handleDateChange({ start, end, label })}
                >
                  <div className="input-group ns-date-input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={
                        dateRange?.startDate && dateRange?.endDate
                          ? `${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`
                          : formatRangeLabel(selectedPreset, dateRange)
                      }
                      readOnly
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <ChevronDown size={16} />
                      </span>
                    </div>
                  </div>
                </DatePicker>
              </div>

              <div className="ns-search-block">
                <div className="ns-search-input-wrap">
                  <Search size={16} className="ns-search-input-wrap__icon" />
                  <input
                    className="form-control ns-search-input"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search guest, email, phone, booking ID, revision..."
                  />
                </div>
                <button className="btn btn-primary ns-primary-btn" type="button" onClick={onRefresh}>
                  Search
                </button>
              </div>

              <div className="ns-command-actions">
                <button className="btn btn-light ns-soft-btn" type="button" onClick={onRefresh} disabled={loading}>
                  <RefreshCw size={16} className={loading ? "ns-spin" : ""} />
                  <span>Refresh</span>
                </button>

                <button className="btn btn-primary ns-primary-btn" type="button" onClick={onSyncNow} disabled={syncing}>
                  <RotateCw size={16} className={syncing ? "ns-spin" : ""} />
                  <span>Sync Now</span>
                </button>

                <button className="btn btn-light ns-soft-btn" type="button" onClick={onRetryFailedAcks} disabled={retrying}>
                  <AlertCircle size={16} className={retrying ? "ns-spin" : ""} />
                  <span>Retry Failed Ack</span>
                </button>

                <div className="ns-download-wrap" onClick={(event) => event.stopPropagation()}>
                  <button
                    className="btn btn-light ns-soft-btn"
                    type="button"
                    onClick={() => setDownloadOpen((prev) => !prev)}
                  >
                    <Download size={16} />
                    <span>Download</span>
                  </button>

                  {downloadOpen ? (
                    <div className="ns-download-menu">
                      <button type="button" className="ns-download-menu__item" onClick={handleExportCSV}>
                        Export CSV
                      </button>
                      <button type="button" className="ns-download-menu__item" onClick={handleExportExcel}>
                        Export Excel
                      </button>
                      <button type="button" className="ns-download-menu__item" onClick={handleOpenPdfPreview}>
                        Print / PDF Preview
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

          </div>

          <div className="ns-filter-row">
   

            <div className="ns-select-grid">
              <div className="ns-select-box">
                <label>OTA Source</label>
                <select className="form-select" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
                  {channelOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ns-select-box">
                <label>Acknowledgement</label>
                <select className="form-select" value={ackFilter} onChange={(event) => setAckFilter(event.target.value)}>
                  {ACK_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ns-select-box">
                <label>StayX Sync</label>
                <select className="form-select" value={syncFilter} onChange={(event) => setSyncFilter(event.target.value)}>
                  {SYNC_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <MetricCard
              title="Pending Acknowledgement"
              value={derivedSummary.pending_ack || 0}
              meta="Revisions waiting to be acknowledged"
              tone="blue"
              icon={<Clock size={18} />}
            />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <MetricCard
              title="Ack Failed"
              value={derivedSummary.failed_ack || 0}
              meta="Items that need retry or review"
              tone="red"
              icon={<AlertCircle size={18} />}
            />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <MetricCard
              title="Synced to StayX"
              value={derivedSummary.synced_to_stayx || 0}
              meta="Bookings already pushed into StayX"
              tone="green"
              icon={<CheckCircle size={18} />}
            />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <MetricCard
              title="Upcoming Bookings"
              value={derivedSummary.upcoming_bookings || 0}
              meta="Future arrivals in the visible dataset"
              tone="amber"
              icon={<Calendar size={18} />}
            />
          </div>
        </div>

        <div className="ns-surface-card">
          <div className="ns-surface-card__head">
            <div>
              <div className="ns-surface-card__title">Booking Activity</div>
              <div className="ns-surface-card__subtitle">
                Monitor incoming revisions, acknowledgements, and StayX actions from one consolidated live table.
              </div>
            </div>
            <div className="ns-surface-card__meta">
              Showing {derivedSummary.total_visible} of {derivedSummary.total_loaded} booking record{derivedSummary.total_loaded === 1 ? "" : "s"}.
            </div>
          </div>

          <div className="ns-tab-row">
            {[
              { key: "all", label: `All (${tabCounts.all})` },
              { key: "upcoming", label: `Upcoming (${tabCounts.upcoming})` },
              { key: "past", label: `Past (${tabCounts.past})` },
              { key: "attention", label: `Attention (${tabCounts.attention})` },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`ns-tab-btn ${activeTab === tab.key ? "ns-tab-btn--active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <Loader label="Pulling booking revisions, statuses, and sync signals from the live feed." />
          ) : filteredRows.length === 0 ? (
            <div className="ns-empty-state">
              <div className="ns-empty-state__icon">
                <Filter size={26} />
              </div>
              <div className="ns-empty-state__title">No bookings found</div>
              <div className="ns-empty-state__copy">
                No OTA bookings match the selected received-date range or filters right now.
              </div>
            </div>
          ) : (
            <div className="table-responsive ns-booking-table-wrap">
              <table className="table ns-booking-table align-middle mb-0">
                <thead>
                  <tr>
                    <th className="ns-sticky-col ns-sticky-col--1">Received</th>
                    <th className="ns-sticky-col ns-sticky-col--2">OTA</th>
                    <th className="ns-sticky-col ns-sticky-col--3">Guest & Stay</th>
                    <th>Guest Preference</th>
                    <th>Room & Plan</th>
                    <th>Expected</th>
                    <th>Amount</th>
                    <th>Ack</th>
                    <th>StayX</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const ackState = getAckState(row);
                    const syncState = getSyncState(row);
                    const revisionId = row.latest_revision_id || row.revision_id;

                    return (
                      <tr key={row.key}>
                        <td className="ns-sticky-col ns-sticky-col--1">
                          <div className="ns-cell-primary">{row.inserted_at_display || formatRelative(row.inserted_at)}</div>
                          <div className="ns-cell-secondary">{formatDateTime(row.inserted_at)}</div>
                        </td>
                        <td className="ns-sticky-col ns-sticky-col--2">
                          <span className="ns-chip ns-chip--blue">{row.ota_name || "—"}</span>
                        </td>
                        <td className="ns-sticky-col ns-sticky-col--3">
                          <div className="ns-cell-primary d-flex align-items-center gap-2 flex-wrap">
                            <span>{row.guest_name || "Guest"}</span>
                            {row.is_genius ? <span className="ns-mini-badge">Genius</span> : null}
                          </div>
                          <div className="ns-cell-secondary">
                            {formatDate(row.arrival_date)} → {formatDate(row.departure_date)}
                          </div>
                          <div className="ns-cell-secondary">{row.occupancy_summary}</div>
                          <div className="ns-cell-secondary">
                            Ref: {row.ota_reservation_code || row.booking_unique_id || row.booking_id || revisionId || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="ns-cell-secondary">
                            {row.smoking_preference ? `Smoking: ${row.smoking_preference}` : "No special preference captured"}
                          </div>
                        </td>
                        <td>
                          <div className="ns-cell-primary">{row.room_type_display || "Room pending"}</div>
                          <div className="ns-cell-secondary">
                            {row.rooms_count || 0} Room{Number(row.rooms_count || 0) > 1 ? "s" : ""}
                            {row.primary_room_guest_name ? ` • Guest: ${row.primary_room_guest_name}` : ""}
                          </div>
                        </td>
                        <td>
                          <div className="ns-cell-primary">    {formatExpectedWindow(row.expected_arrival_text || row.expected_arrival_time)}</div>
                    
                        </td>
                        <td>
                          <div className="ns-cell-primary">{toCurrency(row.amount, row.currency || currencyCode)}</div>
                          <div className="ns-cell-secondary">Paid: {row.amount_paid_label || "—"}


                                  <div className="ns-cell-secondary">
                            {row.payment_collect === "property"
                              ? "Hotel collect"
                              : row.payment_collect === "ota"
                              ? "Collected by OTA"
                              : row.payment_collect || "—"}
                          </div>
                          </div>
                        </td>
                        <td>
                          {ackState === "acked" ? (
                            <span className="ns-chip ns-chip--green">Acknowledged</span>
                          ) : ackState === "failed" ? (
                            <span className="ns-chip ns-chip--red">Ack Failed</span>
                          ) : (
                            <span className="ns-chip ns-chip--amber">Pending</span>
                          )}
                        </td>
                        <td>
                          {syncState === "synced" ? (
                            <span className="ns-chip ns-chip--green">Synced</span>
                          ) : syncState === "failed" ? (
                            <span className="ns-chip ns-chip--red">Sync Failed</span>
                          ) : (
                            <span className="ns-chip ns-chip--slate">Not Synced</span>
                          )}
                        </td>
                        <td>
                          <div className="ns-row-actions">
                            <button type="button" className="ns-icon-btn" onClick={() => openDrawer(row)} title="View detail">
                              <Eye size={16} />
                            </button>
                            <button
                              type="button"
                              className="ns-icon-btn"
                              onClick={() => onAcknowledgeOne(row)}
                              title="Acknowledge"
                              disabled={ackState === "acked" || actionBusy[`ack-${revisionId}`]}
                            >
                              {actionBusy[`ack-${revisionId}`] ? <SpinnerIcon size={16} className="ns-spin" /> : <CheckCircle size={16} />}
                            </button>
                            <button
                              type="button"
                              className="ns-icon-btn ns-icon-btn--primary"
                              onClick={() => onSyncToStayX(row)}
                              title="Sync to StayX"
                              disabled={syncState === "synced" || actionBusy[`sync-${revisionId}`]}
                            >
                              {actionBusy[`sync-${revisionId}`] ? <SpinnerIcon size={16} className="ns-spin" /> : <UploadCloud size={16} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {drawerOpen && selectedRow ? (
          <>
            <div className="ns-drawer-backdrop" onClick={() => setDrawerOpen(false)} />
            <div className="ns-drawer-panel">
              <div className="ns-drawer-panel__header">
                <div>
                  <div className="ns-drawer-panel__eyebrow">Reservation Detail</div>
                  <div className="ns-drawer-panel__title">
                    {selectedRow.guest_name || "Guest"}
                    {selectedRow.is_genius ? <span className="ns-mini-badge ms-2">Genius</span> : null}
                  </div>
                  <div className="ns-drawer-panel__sub">
                    {(selectedRow.ota_name || "OTA")} • Ref {selectedRow.ota_reservation_code || selectedRow.booking_unique_id || selectedRow.booking_id || "—"}
                  </div>
                </div>
                <button type="button" className="ns-drawer-close" onClick={() => setDrawerOpen(false)}>
                  <X size={18} />
                </button>
              </div>

              <div className="ns-drawer-panel__body">
                <div className="ns-detail-hero">
                  <div>
                    <div className="ns-detail-hero__amount-label">Booking Amount</div>
                    <div className="ns-detail-hero__amount">{toCurrency(selectedRow.amount, selectedRow.currency || currencyCode)}</div>
                  </div>
                  <div className="ns-detail-hero__status-group">
                    <span className={selectedRow.status_meta?.className || STATUS_META.unknown.className}>
                      {selectedRow.status_meta?.label || selectedRow.status || "Unknown"}
                    </span>
                    <span className={`ns-chip ${getAckState(selectedRow) === "acked" ? "ns-chip--green" : getAckState(selectedRow) === "failed" ? "ns-chip--red" : "ns-chip--amber"}`}>
                      {getAckState(selectedRow) === "acked" ? "Acknowledged" : getAckState(selectedRow) === "failed" ? "Ack Failed" : "Pending Ack"}
                    </span>
                    <span className={`ns-chip ${getSyncState(selectedRow) === "synced" ? "ns-chip--green" : getSyncState(selectedRow) === "failed" ? "ns-chip--red" : "ns-chip--slate"}`}>
                      {getSyncState(selectedRow) === "synced" ? "StayX Synced" : getSyncState(selectedRow) === "failed" ? "Sync Failed" : "Not Synced"}
                    </span>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-12 col-lg-6">
                    <div className="ns-info-card">
                      <div className="ns-info-card__title">Stay Snapshot</div>
                      <DetailField label="Arrival" value={formatDate(selectedRow.arrival_date)} icon={<Calendar size={14} />} emphasize />
                      <DetailField label="Departure" value={formatDate(selectedRow.departure_date)} icon={<Calendar size={14} />} />
                      <DetailField label="Expected arrival" value={selectedRow.expected_arrival_text || selectedRow.expected_arrival_time} icon={<Clock size={14} />} />
                      <DetailField label="Occupancy" value={selectedRow.occupancy_summary} icon={<User size={14} />} />
                      <DetailField label="Rooms / Nights" value={`${selectedRow.rooms_count || 0} Rooms • ${selectedRow.nights_count || 0} Nights`} />
                      <DetailField label="Room type" value={selectedRow.room_type_display} icon={<Home size={14} />} />
                      <DetailField label="Rate plan" value={selectedRow.rate_plan_display} />
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="ns-info-card">
                      <div className="ns-info-card__title">Primary Guest</div>
                      <DetailField label="Guest" value={selectedRow.guest_name} icon={<User size={14} />} emphasize />
                      <DetailField label="Email" value={selectedRow.guest_email} icon={<Mail size={14} />} />
                      <DetailField label="Phone" value={selectedRow.guest_phone} icon={<Phone size={14} />} />
                      <DetailField label="Country" value={selectedRow.guest_country} />
                      <DetailField label="City" value={selectedRow.guest_city} />
                      <DetailField label="Address" value={selectedRow.guest_address} />
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-12 col-lg-6">
                    <div className="ns-info-card">
                      <div className="ns-info-card__title">Commercial & Payment</div>
                      <DetailField label="Payment type" value={selectedRow.payment_type} icon={<CreditCard size={14} />} emphasize />
                      <DetailField label="Payment collect" value={selectedRow.payment_collect === "property" ? "Hotel collect" : selectedRow.payment_collect === "ota" ? "Collected by OTA" : selectedRow.payment_collect} />
                      <DetailField label="Amount paid" value={selectedRow.amount_paid_label} />
                      <DetailField label="OTA commission" value={selectedRow.ota_commission ? toCurrency(selectedRow.ota_commission, selectedRow.currency) : "—"} />
                      <DetailField label="Special request" value={selectedRow.special_request} />
                      <DetailField label="Meal plan" value={selectedRow.meal_plan_text} />
                      <DetailField label="Smoking preference" value={selectedRow.smoking_preference} />
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="ns-info-card">
                      <div className="ns-info-card__title">Operational Identity</div>
                      <DetailField label="Booking ID" value={selectedRow.booking_id} emphasize />
                      <DetailField label="Revision ID" value={selectedRow.latest_revision_id || selectedRow.revision_id} />
                      <DetailField label="Unique ID" value={selectedRow.booking_unique_id} />
                      <DetailField label="OTA reservation code" value={selectedRow.ota_reservation_code} />
                      <DetailField label="Received" value={selectedRow.inserted_at_display || formatDateTime(selectedRow.inserted_at)} />
                      <DetailField label="Last activity" value={selectedRow.last_activity_at_display || formatDateTime(selectedRow.last_activity_at)} />
                    </div>
                  </div>
                </div>

                {selectedRow.rooms_breakdown?.length ? (
                  <div className="ns-info-card mb-3">
                    <div className="ns-info-card__title">Room Breakdown ({selectedRow.rooms_breakdown.length})</div>
                    <div className="ns-room-grid">
                      {selectedRow.rooms_breakdown.map((room, index) => {
                        const guestNames = Array.isArray(room?.guests)
                          ? room.guests.map((guest) => formatFullName(guest?.name, guest?.surname)).filter(Boolean)
                          : [];

                        const roomOccupancy = room?.occupancy || {};
                        const roomSummary = formatOccupancySummary({
                          adults: roomOccupancy?.adults,
                          children: roomOccupancy?.children,
                          infants: roomOccupancy?.infants,
                        });

                        return (
                          <div key={room.booking_room_id || index} className="ns-room-card">
                            <div className="ns-room-card__top">
                              <div className="ns-room-card__title">Room {index + 1}</div>
                              <div className="ns-room-card__amount">{toCurrency(room.amount, selectedRow.currency || currencyCode)}</div>
                            </div>
                            <DetailField label="Room guests" value={guestNames.join(", ") || selectedRow.primary_room_guest_name} emphasize />
                            <DetailField label="Stay" value={`${formatDate(room.checkin_date)} → ${formatDate(room.checkout_date)}`} />
                            <DetailField label="Occupancy" value={roomSummary} />
                            <DetailField label="Room type" value={room.room_type_display || room.room_type_name || room.room_type_id} />
                            <DetailField label="Rate plan" value={room.rate_plan_display || room.rate_plan_id} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {selectedRow.notes ? (
                  <div className="ns-info-card mb-3">
                    <div className="ns-info-card__title">Notes & Commercial Details</div>
                    <div className="ns-note-block">{selectedRow.notes}</div>
                  </div>
                ) : null}

                <div className="ns-info-card mb-3">
                  <div className="ns-info-card__title">Available Actions</div>
                  <div className="ns-drawer-actions">
                    <button
                      type="button"
                      className="btn btn-light ns-soft-btn"
                      onClick={() => onAcknowledgeOne(selectedRow)}
                      disabled={getAckState(selectedRow) === "acked" || actionBusy[`ack-${selectedRow.latest_revision_id || selectedRow.revision_id}`]}
                    >
                      {actionBusy[`ack-${selectedRow.latest_revision_id || selectedRow.revision_id}`] ? <SpinnerIcon size={16} className="ns-spin" /> : <CheckCircle size={16} />}
                      <span>Acknowledge</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary ns-primary-btn"
                      onClick={() => onSyncToStayX(selectedRow)}
                      disabled={getSyncState(selectedRow) === "synced" || actionBusy[`sync-${selectedRow.latest_revision_id || selectedRow.revision_id}`]}
                    >
                      {actionBusy[`sync-${selectedRow.latest_revision_id || selectedRow.revision_id}`] ? <SpinnerIcon size={16} className="ns-spin" /> : <UploadCloud size={16} />}
                      <span>Sync to StayX</span>
                    </button>
                  </div>
                </div>

                <details className="ns-debug-panel">
                  <summary>Raw Payload (Debug)</summary>
                  <pre>{JSON.stringify(selectedRow.raw_payload || {}, null, 2)}</pre>
                </details>
              </div>
            </div>
          </>
        ) : null}

        <PrintReportPreview
          visible={pdfVisible}
          onClose={() => setPdfVisible(false)}
          reportTitle="OTA Booking List"
          propertyName={propertyName}
          data={buildExportData(filteredRows, currencyCode)}
          headers={exportHeaders}
          footerData={totalsForPageFooter}
          totalColumns={["guests", "nights"]}
          onDownloadPDF={handleExportPDF}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={process.env.REACT_APP_API_URL}
        />

        <style>{`
          .ns-ota-page {
            color: #0f172a;
            padding-bottom: 24px;
          }

          .ns-badge-inline {
            display: inline-flex;
            align-items: center;
            padding: 7px 12px;
            border-radius: 999px;
            background: rgba(37, 99, 235, 0.08);
            color: #1d4ed8;
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 0.3px;
            margin-bottom: 14px;
          }

          .ns-hero-panel {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 210px;
            gap: 14px;
            padding: 18px 20px 14px;
            border-radius: 24px;
            background:
              radial-gradient(circle at top right, rgba(37, 99, 235, 0.10), transparent 35%),
              linear-gradient(180deg, #ffffff, #f8fbff);
            border: 1px solid #e8eef7;
            box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08);
            margin-bottom: 18px;
          }

          .ns-page-title {
            margin: 0 0 6px;
            font-size: 34px;
            line-height: 1.02;
            font-weight: 800;
            letter-spacing: -0.03em;
            color: #0f172a;
          }

          .ns-page-subtitle {
            margin: 0;
            max-width: 760px;
            font-size: 14px;
            line-height: 1.6;
            color: rgba(15, 23, 42, 0.70);
          }

          .ns-hero-panel__meta {
            display: grid;
            gap: 10px;
            align-content: start;
          }

          .ns-mini-stat {
            border-radius: 16px;
            border: 1px solid #edf2f7;
            background: rgba(255, 255, 255, 0.92);
            padding: 14px 16px;
            box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
          }

          .ns-mini-stat__label {
            font-size: 12px;
            font-weight: 700;
            color: rgba(15, 23, 42, 0.56);
            margin-bottom: 3px;
          }

          .ns-mini-stat__value {
            font-size: 18px;
            line-height: 1.15;
            font-weight: 800;
            color: #0f172a;
          }

          .ns-command-card,
          .ns-surface-card,
          .ns-info-card,
          .ns-metric-card {
            background: #fff;
            border: 1px solid #eaf0f7;
            box-shadow: 0 16px 48px rgba(15, 23, 42, 0.06);
          }

          .ns-command-card,
          .ns-surface-card,
          .ns-info-card {
            border-radius: 24px;
          }

          .ns-command-card {
            margin-bottom: 18px;
            overflow: visible;
          }

          .ns-command-card__top {
            padding: 22px 22px 16px;
            border-bottom: 1px solid #eef2f7;
          }

          .ns-toolbar-grid {
            display: grid;
            grid-template-columns: minmax(280px, 320px) minmax(340px, 1fr) auto;
            gap: 14px;
            align-items: center;
          }

          .ns-date-picker-wrap,
          .ns-search-block,
          .ns-command-actions {
            min-width: 0;
          }

          .ns-date-input-group .form-control,
          .ns-date-input-group .input-group-text,
          .ns-search-input,
          .ns-select-box .form-select {
            height: 46px;
            border-color: #d9e3ef;
            box-shadow: none !important;
          }

          .ns-date-input-group .form-control {
            border-right: none;
            border-radius: 14px 0 0 14px;
            font-size: 15px;
            font-weight: 800;
            text-align: center;
            background: #fff;
            cursor: pointer;
          }

          .ns-date-input-group .input-group-text {
            background: #fff;
            border-left: none;
            border-radius: 0 14px 14px 0;
            cursor: pointer;
            padding-inline: 16px;
          }

          .ns-search-block {
            display: grid;
            grid-template-columns: 1fr 110px;
            gap: 0;
          }

          .ns-search-input-wrap {
            position: relative;
          }

          .ns-search-input-wrap__icon {
            position: absolute;
            top: 50%;
            left: 14px;
            transform: translateY(-50%);
            color: rgba(15, 23, 42, 0.44);
          }

          .ns-search-input {
            border-radius: 14px 0 0 14px;
            padding-left: 42px;
          }

          .ns-primary-btn,
          .ns-soft-btn {
            height: 46px;
            border-radius: 14px !important;
            font-weight: 800;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding-inline: 16px;
          }

          .ns-primary-btn {
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
            border-color: #2563eb;
            box-shadow: 0 12px 24px rgba(37, 99, 235, 0.18);
          }

          .ns-soft-btn {
            background: #fff;
            border-color: #d9e3ef;
            color: #0f172a;
          }

          .ns-command-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 10px;
          }

          .ns-download-wrap {
            position: relative;
          }

          .ns-download-menu {
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            min-width: 210px;
            background: #fff;
            border: 1px solid #e8eef7;
            border-radius: 16px;
            box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
            overflow: hidden;
            z-index: 30;
          }

          .ns-download-menu__item {
            width: 100%;
            border: 0;
            background: #fff;
            text-align: left;
            padding: 12px 14px;
            font-weight: 700;
            color: #0f172a;
          }

          .ns-download-menu__item:hover {
            background: #f8fbff;
          }

          .ns-quick-preset-row {
            margin-top: 14px;
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }

          .ns-chip-btn,
          .ns-pill-filter {
            height: 36px;
            border-radius: 999px;
            border: 1px solid #d9e3ef;
            background: #fff;
            color: #334155;
            font-weight: 800;
            padding-inline: 14px;
            transition: all 180ms ease;
          }

          .ns-chip-btn:hover,
          .ns-pill-filter:hover {
            border-color: #b7cae4;
            transform: translateY(-1px);
          }

          .ns-chip-btn--active,
          .ns-pill-filter--active {
            background: #2563eb;
            border-color: #2563eb;
            color: #fff;
            box-shadow: 0 10px 20px rgba(37, 99, 235, 0.18);
          }

          .ns-filter-row {
            padding: 18px 22px 22px;
          }

          .ns-filter-head {
            margin-bottom: 12px;
          }

          .ns-filter-head__title {
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.35px;
            color: rgba(15, 23, 42, 0.56);
            font-weight: 800;
            margin-bottom: 2px;
          }

          .ns-filter-head__sub {
            font-size: 13px;
            color: rgba(15, 23, 42, 0.62);
          }

          .ns-pill-filter-row {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 10px;
          }

          .ns-select-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 14px;
          }

          .ns-select-box label {
            display: block;
            font-size: 12px;
            font-weight: 800;
            color: rgba(15, 23, 42, 0.56);
            margin-bottom: 6px;
            text-transform: uppercase;
            letter-spacing: 0.25px;
          }

          .ns-metric-card {
            border-radius: 22px;
            min-height: 136px;
            padding: 18px;
          }

          .ns-metric-card__top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 12px;
          }

          .ns-metric-card__title {
            font-size: 13px;
            color: rgba(15, 23, 42, 0.58);
            font-weight: 700;
            margin-bottom: 6px;
          }

          .ns-metric-card__value {
            font-size: 34px;
            line-height: 1;
            font-weight: 800;
            color: #0f172a;
          }

          .ns-metric-card__meta {
            font-size: 12px;
            line-height: 1.55;
            color: rgba(15, 23, 42, 0.56);
          }

          .ns-metric-card__icon {
            width: 42px;
            height: 42px;
            border-radius: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .ns-metric-card--blue { background: linear-gradient(180deg, #ffffff, #f8fbff); }
          .ns-metric-card--red { background: linear-gradient(180deg, #ffffff, #fff7f7); }
          .ns-metric-card--green { background: linear-gradient(180deg, #ffffff, #f7fffb); }
          .ns-metric-card--amber { background: linear-gradient(180deg, #ffffff, #fffdf7); }

          .ns-metric-card__icon--blue { background: #eaf2ff; color: #2563eb; }
          .ns-metric-card__icon--red { background: #fff1f2; color: #dc2626; }
          .ns-metric-card__icon--green { background: #ecfdf5; color: #059669; }
          .ns-metric-card__icon--amber { background: #fff7ed; color: #ea580c; }

          .ns-surface-card {
            padding: 22px;
          }

          .ns-surface-card__head {
            margin-bottom: 14px;
          }

          .ns-surface-card__title {
            font-size: 18px;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 4px;
          }

          .ns-surface-card__subtitle {
            font-size: 14px;
            color: rgba(15, 23, 42, 0.62);
            line-height: 1.6;
          }

          .ns-tab-row {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            margin-bottom: 16px;
          }

          .ns-tab-btn {
            border: 0;
            border-bottom: 2px solid transparent;
            background: transparent;
            color: #64748b;
            font-weight: 800;
            padding: 6px 2px;
            margin-right: 18px;
          }

          .ns-tab-btn--active {
            color: #2563eb;
            border-bottom-color: #2563eb;
          }

          .ns-booking-table-wrap {
            border: 1px solid #ecf1f7;
            border-radius: 18px;
            overflow-x: auto;
            overflow-y: hidden;
            position: relative;
            background: #fff;
          }

          .ns-booking-table {
            margin: 0;
            min-width: 1680px;
            table-layout: fixed;
          }

          .ns-booking-table thead th {
            vertical-align: middle;
            white-space: nowrap;
            border-bottom: 1px solid #e8eef7;
            background: #f8fbff;
            color: #64748b;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.32px;
            font-weight: 800;
            padding: 16px 14px;
          }

          .ns-booking-table tbody td {
            padding: 16px 14px;
            border-bottom: 1px solid #eef3f8;
            background: #fff;
          }

          .ns-booking-table tbody tr:hover td {
            background: #fbfdff;
          }


          .ns-booking-table th,
          .ns-booking-table td {
            min-width: 120px;
          }

          .ns-booking-table .ns-sticky-col {
            position: sticky;
            left: 0;
            z-index: 3;
            background: #fff;
          }

          .ns-booking-table thead .ns-sticky-col {
            z-index: 5;
            background: #f8fbff;
          }

          .ns-booking-table .ns-sticky-col--1 {
            left: 0;
            min-width: 150px;
            width: 150px;
            box-shadow: 1px 0 0 #eef3f8;
          }

          .ns-booking-table .ns-sticky-col--2 {
            left: 150px;
            min-width: 120px;
            width: 120px;
            box-shadow: 1px 0 0 #eef3f8;
          }

          .ns-booking-table .ns-sticky-col--3 {
            left: 270px;
            min-width: 290px;
            width: 290px;
            box-shadow: 12px 0 18px -16px rgba(15, 23, 42, 0.22);
          }

          .ns-cell-primary {
            font-size: 14px;
            font-weight: 800;
            color: #0f172a;
            line-height: 1.5;
          }

          .ns-cell-secondary {
            margin-top: 3px;
            font-size: 12px;
            line-height: 1.55;
            color: rgba(15, 23, 42, 0.60);
          }

          .ns-cell-note {
            margin-top: 4px;
            font-size: 12px;
            line-height: 1.5;
            color: #1d4ed8;
            font-weight: 700;
          }

          .ns-mini-badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 8px;
            border-radius: 999px;
            background: #fef3c7;
            color: #92400e;
            font-size: 11px;
            font-weight: 800;
          }

          .ns-chip {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 30px;
            padding: 4px 10px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 800;
            white-space: nowrap;
          }

          .ns-chip--blue { background: #eaf2ff; color: #2563eb; }
          .ns-chip--green { background: #ecfdf5; color: #059669; }
          .ns-chip--amber { background: #fff7ed; color: #d97706; }
          .ns-chip--red { background: #fff1f2; color: #dc2626; }
          .ns-chip--slate { background: #f1f5f9; color: #475569; }

          .ns-row-actions {
            display: flex;
            justify-content: flex-end;
            gap: 8px;
          }

          .ns-icon-btn {
            width: 36px;
            min-width: 36px;
            height: 36px;
            border-radius: 999px;
            border: 1px solid #d9e3ef;
            background: #fff;
            color: #0f172a;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: all 160ms ease;
          }

          .ns-icon-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            border-color: #b7cae4;
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
          }

          .ns-icon-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }

          .ns-icon-btn--primary {
            border-color: #2563eb;
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
            color: #fff;
            box-shadow: 0 10px 18px rgba(37, 99, 235, 0.18);
          }

          .ns-loader-card {
            min-height: 360px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 24px;
          }

          .ns-loader-ring {
            width: 68px;
            height: 68px;
            border-radius: 999px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(180deg, #eff6ff, #dbeafe);
            color: #1d4ed8;
            margin-bottom: 16px;
            box-shadow: 0 16px 32px rgba(37, 99, 235, 0.18);
          }

          .ns-loader-title {
            font-size: 20px;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 6px;
          }

          .ns-loader-copy {
            max-width: 520px;
            font-size: 14px;
            line-height: 1.7;
            color: rgba(15, 23, 42, 0.62);
          }

          .ns-spin {
            animation: nsSpin 1s linear infinite;
          }

          @keyframes nsSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .ns-empty-state {
            min-height: 320px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 24px;
          }

          .ns-empty-state__icon {
            width: 64px;
            height: 64px;
            border-radius: 20px;
            background: #f8fafc;
            color: #64748b;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 16px;
          }

          .ns-empty-state__title {
            font-size: 20px;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 6px;
          }

          .ns-empty-state__copy {
            max-width: 520px;
            font-size: 14px;
            line-height: 1.7;
            color: rgba(15, 23, 42, 0.62);
          }

          .ns-drawer-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.44);
            z-index: 1600;
            backdrop-filter: blur(4px);
          }

          .ns-drawer-panel {
            position: fixed;
            top: 0;
            right: 0;
            width: min(960px, 100vw);
            height: 100vh;
            background: #f8fbff;
            z-index: 1601;
            box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
            display: flex;
            flex-direction: column;
          }

          .ns-drawer-panel__header {
            position: sticky;
            top: 0;
            z-index: 2;
            padding: 20px 22px;
            background: rgba(255, 255, 255, 0.96);
            backdrop-filter: blur(6px);
            border-bottom: 1px solid #e8eef7;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 14px;
          }

          .ns-drawer-panel__eyebrow {
            font-size: 12px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.35px;
            color: rgba(15, 23, 42, 0.58);
            margin-bottom: 4px;
          }

          .ns-drawer-panel__title {
            font-size: 28px;
            line-height: 1.08;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 6px;
          }

          .ns-drawer-panel__sub {
            font-size: 14px;
            color: rgba(15, 23, 42, 0.62);
            line-height: 1.6;
          }

          .ns-drawer-close {
            width: 38px;
            height: 38px;
            border-radius: 999px;
            border: 1px solid #d9e3ef;
            background: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: #0f172a;
          }

          .ns-drawer-panel__body {
            flex: 1;
            overflow: auto;
            padding: 20px 22px 28px;
          }

          .ns-detail-hero {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            flex-wrap: wrap;
            padding: 22px;
            border-radius: 24px;
            background: linear-gradient(180deg, #ffffff, #fbfdff);
            border: 1px solid #e8eef7;
            box-shadow: 0 14px 40px rgba(15, 23, 42, 0.06);
            margin-bottom: 16px;
          }

          .ns-detail-hero__amount-label {
            font-size: 12px;
            font-weight: 700;
            color: rgba(15, 23, 42, 0.56);
            margin-bottom: 4px;
          }

          .ns-detail-hero__amount {
            font-size: 30px;
            line-height: 1.05;
            font-weight: 800;
            color: #0f172a;
          }

          .ns-detail-hero__status-group {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .ns-info-card {
            border-radius: 20px;
            padding: 18px;
            height: 100%;
          }

          .ns-info-card__title {
            font-size: 14px;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 12px;
          }

          .ns-detail-field + .ns-detail-field {
            margin-top: 12px;
          }

          .ns-detail-field__label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: rgba(15, 23, 42, 0.58);
            font-weight: 700;
            margin-bottom: 4px;
          }

          .ns-detail-field__value {
            font-size: 14px;
            line-height: 1.65;
            color: #0f172a;
            word-break: break-word;
          }

          .ns-detail-field__value--strong {
            font-weight: 800;
          }

          .ns-room-grid {
            display: grid;
            gap: 14px;
          }

          .ns-room-card {
            border-radius: 18px;
            border: 1px solid #e8eef7;
            background: linear-gradient(180deg, #ffffff, #fbfdff);
            padding: 16px;
          }

          .ns-room-card__top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 12px;
          }

          .ns-room-card__title {
            font-size: 16px;
            font-weight: 800;
            color: #0f172a;
          }

          .ns-room-card__amount {
            font-size: 16px;
            font-weight: 800;
            color: #0f172a;
          }

          .ns-note-block {
            white-space: pre-wrap;
            font-size: 14px;
            line-height: 1.75;
            color: #0f172a;
          }

          .ns-drawer-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          }

          .ns-debug-panel {
            border-radius: 16px;
            border: 1px solid #e8eef7;
            background: #fff;
            padding: 0;
            overflow: hidden;
          }

          .ns-debug-panel summary {
            cursor: pointer;
            padding: 14px 16px;
            font-weight: 800;
            color: #334155;
          }

          .ns-debug-panel pre {
            margin: 0;
            padding: 0 16px 16px;
            font-size: 12px;
            line-height: 1.65;
            white-space: pre-wrap;
            word-break: break-word;
          }

          @media (max-width: 1280px) {
            .ns-hero-panel {
              grid-template-columns: 1fr;
            }

            .ns-toolbar-grid {
              grid-template-columns: 1fr;
            }

            .ns-command-actions {
              justify-content: flex-start;
            }
          }

          @media (max-width: 768px) {
            .ns-page-title {
              font-size: 34px;
            }

            .ns-select-grid {
              grid-template-columns: 1fr;
            }

            .ns-search-block {
              grid-template-columns: 1fr 92px;
            }

            .ns-detail-hero {
              padding: 18px;
            }

            .ns-drawer-panel {
              width: 100vw;
            }

            .ns-drawer-panel__title {
              font-size: 24px;
            }
          }
        `}</style>
      </div>
    </div>
  );
}
